import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { initializeApp } from 'firebase/app';
import { initializeAnalytics as initializeFirebaseWebAnalytics } from 'firebase/analytics';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import type { History } from 'history';
import type { ProvisioningMode } from '../provisioning/deviceProvisioning';

// Public identifiers for the OnlyCat Firebase project's web app (not secrets).
// Native platforms configure themselves from google-services.json and
// GoogleService-Info.plist instead.
const FIREBASE_WEB_CONFIG = {
    apiKey: 'AIzaSyBllLM4RMNbVT6D5_9JO3KAakWu5cL3xrI',
    authDomain: 'onlycat.firebaseapp.com',
    projectId: 'onlycat',
    storageBucket: 'onlycat.firebasestorage.app',
    messagingSenderId: '95234456997',
    appId: '1:95234456997:web:e1a4a4a9359bcbf5213c4c',
    measurementId: 'G-JF5B8VGDEQ'
};

export type ProvisioningStep =
    | 'connect'
    | 'setup_mode_wait'
    | 'wifi_scan'
    | 'wifi_join'
    | 'claim'
    | 'qr_scan';

/**
 * Every custom event the app logs, with its typed parameters. Add new events
 * here rather than calling FirebaseAnalytics.logEvent directly, so names and
 * parameters stay consistent across the app. Parameter values must never
 * contain personal data (SSIDs, passwords, email addresses, free-form text).
 */
export type AnalyticsEvent =
    | { name: 'login' }
    | { name: 'provisioning_started'; params: { mode: ProvisioningMode } }
    | { name: 'provisioning_step'; params: { mode: ProvisioningMode; step: 'connected' | 'setup_mode_entered' | 'wifi_join_started' | 'wifi_joined' } }
    | { name: 'provisioning_error'; params: { mode: ProvisioningMode | 'none'; step: ProvisioningStep; code: string } }
    | { name: 'provisioning_completed'; params: { mode: ProvisioningMode } }
    | { name: 'provisioning_abandoned'; params: { mode: ProvisioningMode | 'none'; step: string } }
    | { name: 'api_error'; params: { method: string; code: string } }
    | { name: 'video_error'; params: { context: 'event_playback' } }
    | { name: 'app_error'; params: { context: string; message: string } }
    | { name: 'web_vitals'; params: { metric: string; value: number; rating: string } };

const isNative = Capacitor.isNativePlatform();

// Analytics must never break the app: every entry point swallows its own
// failures (e.g. Firebase blocked by a content blocker on web).
function swallow(promise: Promise<unknown>): void {
    promise.catch(error => console.warn('(OCAnalytics) call failed', error));
}

export function initializeAnalytics(): void {
    try {
        // Store-screenshot capture runs must not pollute production analytics.
        if (import.meta.env.MODE === 'screenshot') {
            return;
        }

        if (!isNative) {
            const app = initializeApp(FIREBASE_WEB_CONFIG);
            // gtag attaches the live URL and document.title to every hit by
            // default, which would leak device ids (from paths) and device or
            // event names (from titles) into GA. Pin both, and drop the
            // automatic page_view — screen_view from installScreenTracking is
            // the one source of truth for views on every platform.
            initializeFirebaseWebAnalytics(app, {
                config: {
                    send_page_view: false,
                    page_title: 'OnlyCat',
                    page_location: window.location.origin + '/'
                }
            });
        }

        // Consent is presumed for now (limited test group); a consent gate can
        // flip this off at startup later without touching any call sites.
        swallow(FirebaseAnalytics.setEnabled({ enabled: true }));

        // The Capgo live-update bundle version differs from the store version,
        // so stamp it on both analytics and crash reports.
        swallow(CapacitorUpdater.current().then(({ bundle }) => {
            const version = bundle?.version || 'builtin';
            swallow(FirebaseAnalytics.setUserProperty({ key: 'capgo_bundle', value: version }));
            if (isNative) {
                swallow(FirebaseCrashlytics.setCustomKey({ key: 'capgo_bundle', value: version, type: 'string' }));
            }
        }));
    } catch (error) {
        console.warn('(OCAnalytics) initialization failed', error);
    }
}

export function trackScreen(screenName: string): void {
    try {
        // Without the override, native screen_views carry the host controller
        // (MyViewController/MainActivity) as their class, which is what GA's
        // default "screen class" reporting dimension displays.
        swallow(FirebaseAnalytics.setCurrentScreen({ screenName, screenClassOverride: screenName }));
    } catch (error) {
        console.warn('(OCAnalytics) trackScreen failed', error);
    }
}

export function trackEvent(event: AnalyticsEvent): void {
    try {
        swallow(FirebaseAnalytics.logEvent({
            name: event.name,
            params: 'params' in event ? event.params : undefined
        }));
    } catch (error) {
        console.warn('(OCAnalytics) trackEvent failed', error);
    }
}

/** Associates events and crash reports with the gateway user id (null on logout). */
export function setAnalyticsUser(userId: string | null): void {
    try {
        swallow(FirebaseAnalytics.setUserId({ userId }));
        if (isNative) {
            swallow(FirebaseCrashlytics.setUserId({ userId: userId ?? '' }));
        }
    } catch (error) {
        console.warn('(OCAnalytics) setAnalyticsUser failed', error);
    }
}

/**
 * Records a non-fatal error. On native platforms it lands in Crashlytics
 * (with the JS stack attached as a log breadcrumb); everywhere it is also
 * logged as an `app_error` analytics event so affected sessions show up in GA.
 */
export function recordError(error: unknown, context: string): void {
    try {
        const message = error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error ?? 'unknown');

        if (isNative) {
            const stack = error instanceof Error ? error.stack : undefined;
            if (stack) {
                swallow(FirebaseCrashlytics.log({ message: stack }));
            }
            swallow(FirebaseCrashlytics.recordException({ message: `[${context}] ${message}` }));
        }

        trackEvent({
            name: 'app_error',
            params: {
                context,
                // GA4 truncates param values at 100 characters; cut deliberately
                // so the context prefix always survives.
                message: message.slice(0, 100)
            }
        });
    } catch (recordingError) {
        console.warn('(OCAnalytics) recordError failed', recordingError);
    }
}

export function installGlobalErrorReporting(): void {
    window.addEventListener('error', event => {
        recordError(event.error ?? event.message, 'window_error');
    });

    window.addEventListener('unhandledrejection', event => {
        recordError(event.reason, 'unhandled_rejection');
    });
}

/** Maps a router location onto a stable, non-PII screen name. */
export function screenNameForLocation(pathname: string, search: string): string {
    // The event modal is URL-driven in two forms (see utils/eventModalLocation):
    // the canonical /events/:deviceId/:eventId path or an `event` search param
    // over any other screen. Both present as the same full-screen modal.
    if (/^\/events\/./.test(pathname) || new URLSearchParams(search).has('event')) return 'event';
    if (pathname === '/' || pathname.startsWith('/activity') || pathname.startsWith('/events')) return 'activity';
    if (/^\/devices\/./.test(pathname)) return 'device';
    if (pathname.startsWith('/devices')) return 'devices';
    if (pathname.startsWith('/account/delete')) return 'account_delete';
    if (pathname.startsWith('/account/invite')) return 'account_invite';
    if (pathname.startsWith('/account')) return 'account';
    if (pathname.startsWith('/payment/success')) return 'payment_success';
    if (pathname.startsWith('/payment/cancel')) return 'payment_cancel';
    if (pathname.startsWith('/payment/return')) return 'payment_return';
    if (pathname.startsWith('/pre-shipping-questionnaire')) return 'pre_shipping_questionnaire';
    return 'unknown';
}

/** Logs a screen_view for the current location and every navigation after it. */
export function installScreenTracking(history: History): void {
    let lastScreen: string | undefined;

    const track = (pathname: string, search: string) => {
        const screenName = screenNameForLocation(pathname, search);
        if (screenName === lastScreen) {
            return;
        }
        lastScreen = screenName;
        trackScreen(screenName);
    };

    track(history.location.pathname, history.location.search);
    history.listen(({ location }) => track(location.pathname, location.search));
}
